import { Fragment, useState } from "react";
import LayersIcon from "@mui/icons-material/Layers";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import { OrderTypes, ShiftRepeat } from "@repo/database";
import { getDateFNSLocale } from "@repo/utils";
import { OrderShiftSchema, ShiftSchema } from "@repo/validators";
import { addMinutes, differenceInMinutes, format, set } from "date-fns";
import { ChevronsLeftRightEllipsis } from "lucide-react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { OrderShift } from "~/providers/shift-provider";
import { useDragScroll } from "~/hooks/use-drag-scroll";
import { useSection } from "~/hooks/use-section";
import { cn } from "~/lib/utils";
import { useShiftManager } from "~/managers/use-shift-manager";
import { useService } from "~/providers/service-provider";
import { Button } from "../../ui/button";
import { Calendar } from "../../ui/calendar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../../ui/form";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { ModalInput } from "../../ui/modal-input";
import { MultiSelect } from "../../ui/multi-select";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import {
  SelectContent,
  SelectItem,
  SelectValue,
  SelectWithTrigger,
} from "../../ui/select";
import { Switch } from "../../ui/switch";
import TimePicker from "../../ui/time-picker";
import VrodexPopover from "../../ui/vrodex-popover";

const SHIFT_FORM_SECTIONS = {
  service: "service",
  time_and_day: "time_and_day",
  seating: "seating",
  pacing: "pacing",
} as const;

type ShiftFormSection = keyof typeof SHIFT_FORM_SECTIONS;

function Timeslots({
  startTime,
  endTime,
  maxGuests,
  defaultDuration,
  timeslotInterval,
  value,
  onChange,
}: {
  startTime: Date;
  endTime: Date;
  maxGuests: number;
  defaultDuration: number;
  timeslotInterval: number;
  value: { start: Date; spots: number; duration: number; closed: boolean }[];
  onChange?: (
    updatedValue: { start: Date; spots: number; duration: number }[],
  ) => void;
}) {
  const { t } = useTranslation("common");

  const minutesBetween = differenceInMinutes(endTime, startTime);

  const amountOfTimeslots = Math.floor(
    Math.abs(minutesBetween / timeslotInterval),
  );

  const handleInputChange = (
    index: number,
    field: "spots" | "duration" | "closed",
    newValue: number | boolean,
  ) => {
    const updatedValue = [...value];

    const slotTime = addMinutes(startTime, index * timeslotInterval);

    updatedValue[index] = {
      ...updatedValue[index], // Preserve existing values
      start: slotTime,
      [field]: newValue,
    };

    console.log(updatedValue);

    onChange?.(updatedValue);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center border-b p-4">
        <div className="flex-1">
          <Label>{t("Shifts.timeslot.label")}</Label>
        </div>
        <div className="w-[100px]">
          <Label>{t("Shifts.timeslot.spots")}</Label>
        </div>
        <div className="w-[100px]">
          <Label>{t("Shifts.timeslot.duration")}</Label>
        </div>
        <div>
          <Label>{t("Shifts.timeslot.enabled")}</Label>
        </div>
      </div>
      <div className="p-4 flex flex-col gap-4">
        {Array.from({ length: amountOfTimeslots + 1 }).map((_, idx) => {
          const slotTime = addMinutes(startTime, idx * timeslotInterval);
          const spotsValue = value[idx]?.spots ?? maxGuests;
          const durationValue = value[idx]?.duration ?? defaultDuration;
          const closedValue = value[idx]?.closed ?? false;
          return (
            <Fragment key={idx}>
              <div className="flex flex-row items-center">
                <div className="flex-1">
                  <Label size="lg">{format(slotTime, "HH:mm")}</Label>
                </div>

                <div className="flex flex-row gap-2 items-center">
                  <Input
                    value={spotsValue}
                    onChange={(e) =>
                      handleInputChange(
                        idx,
                        "spots",
                        Number(e.target.value) || 0,
                      )
                    }
                    className="max-w-[100px]"
                  />

                  <Input
                    value={durationValue}
                    onChange={(e) =>
                      handleInputChange(
                        idx,
                        "duration",
                        Number(e.target.value) || defaultDuration,
                      )
                    }
                    className="max-w-[100px]"
                  />
                  <Switch
                    defaultChecked={!closedValue}
                    onCheckedChange={(checked) =>
                      handleInputChange(idx, "closed", !checked)
                    }
                  />
                </div>
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default function OrderShiftForm({
  shift,
  onClose,
}: {
  shift?: OrderShift;
  onClose: () => void;
}) {
  const { updateOrderShift, createOrderShift } = useShiftManager();

  const [startTimeOpen, setStartTimeOpen] = useState(false);

  const [endTimeOpen, setEndTimeOpen] = useState(false);

  const [dateOpen, setDateOpen] = useState(false);

  const [endDateOpen, setEndDateOpen] = useState(false);

  const { t, i18n } = useTranslation("common");

  const repeatOptions = [
    {
      label: t("Shift.repeat_never"),
      value: ShiftRepeat.never,
    },
    {
      label: t("Shift.repeat_weekly"),
      value: ShiftRepeat.weekly,
    },
  ];

  const repeatOnOptions = [
    {
      label: t("Days.monday_short"),
      value: 1,
    },
    {
      label: t("Days.tuesday_short"),
      value: 2,
    },
    {
      label: t("Days.wednesday_short"),
      value: 3,
    },
    {
      label: t("Days.thursday_short"),
      value: 4,
    },
    {
      label: t("Days.friday_short"),
      value: 5,
    },
    {
      label: t("Days.saturday_short"),
      value: 6,
    },
    {
      label: t("Days.sunday_short"),
      value: 0,
    },
  ];

  const orderTypeOptions = [
    {
      label: t("OrderTypes.delivery"),
      value: OrderTypes.delivery,
    },
    {
      label: t("OrderTypes.pickup"),
      value: OrderTypes.pickup,
    },
  ];

  const timeslotIntervalOptions = [
    {
      label: t("Time.x_minutes_short", { x: 15 }),
      value: 15,
    },
    {
      label: t("Time.x_minutes_short", { x: 30 }),
      value: 30,
    },
  ];

  const form = useForm({
    schema: OrderShiftSchema(i18n),
    defaultValues: {
      startTime:
        shift?.startTime ??
        set(new Date(), { hours: 7, minutes: 0, seconds: 0, milliseconds: 0 }),
      orderType: shift?.orderType ?? undefined,
      endTime:
        shift?.endTime ??
        set(new Date(), { hours: 22, minutes: 0, seconds: 0, milliseconds: 0 }),
      endDate: shift?.endDate ?? undefined,
      startDate:
        shift?.startDate ??
        set(new Date(), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }),
      timeslotInterval: shift?.timeslotInterval ?? 15,
      repeat: shift?.repeat ?? ShiftRepeat.weekly,
      repeatDays: shift?.repeatDays ?? [],
      timeslots:
        shift?.timeslots.filter((timeslot) => timeslot.temporary === false) ??
        [],
    },
  });

  const startTime = useWatch({ control: form.control, name: "startTime" });

  const endTime = useWatch({ control: form.control, name: "endTime" });

  const repeat = useWatch({ control: form.control, name: "repeat" });

  const endDate = useWatch({ control: form.control, name: "endDate" });

  const orderType = useWatch({ control: form.control, name: "orderType" });

  const timeslotInterval = useWatch({
    control: form.control,
    name: "timeslotInterval",
  });

  const repeatDays = useWatch({ control: form.control, name: "repeatDays" });

  const [selectedSection, setSelectedSection] = useState<ShiftFormSection>(
    SHIFT_FORM_SECTIONS.service,
  );

  const shiftFormSections = [
    {
      section: SHIFT_FORM_SECTIONS.service,
      label: t("service"),
      value: orderType,
      icon: <ScheduleIcon />,
    },
    {
      section: SHIFT_FORM_SECTIONS.time_and_day,
      label: t("time_and_days"),
      value: `${format(form.getValues("startTime"), "HH:mm")} - ${format(form.getValues("endTime"), "HH:mm")}`,
      icon: <ScheduleIcon />,
    },
    {
      section: SHIFT_FORM_SECTIONS.pacing,
      label: t("Shifts.timeslots"),
      value: "",
      icon: <ChevronsLeftRightEllipsis />,
    },
  ];

  const handleSubmit = form.handleSubmit(async (formData) => {
    if (shift) {
      const result = await updateOrderShift({
        ...formData,
        id: shift.id,
      });
      if (result === true) {
        onClose();
      }
    } else {
      const result = await createOrderShift(formData);
      if (result === true) {
        onClose();
      }
    }
  });

  const calculateTimeslots = ({
    field,
    date,
    duration,
    maxGuests,
  }: {
    field: "start" | "end" | "duration" | "capacity";
    date?: Date;
    duration?: number;
    maxGuests?: number;
  }) => {
    let minutesBetween = 0;

    if (date) {
      if (field === "start") {
        minutesBetween = differenceInMinutes(endTime, date);
      } else {
        console.log(startTime);
        minutesBetween = differenceInMinutes(date, startTime);
      }
    } else {
      minutesBetween = differenceInMinutes(endTime, startTime);
    }

    const amountOfTimeslots = Math.floor(
      Math.abs(minutesBetween / timeslotInterval),
    );

    const timeslots = Array.from({ length: amountOfTimeslots + 1 }).map(
      (_, idx) => {
        const slotTime = addMinutes(startTime, idx * timeslotInterval);
        return {
          start: slotTime,
          spots,
          closed: false,
        };
      },
    );

    form.setValue("timeslots", timeslots);
  };

  const [ref] = useDragScroll();

  return (
    <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
      <div
        ref={ref}
        className="flex md:hidden flex-row overflow-x-auto small-scrollbar min-h-[100px]"
      >
        {shiftFormSections.map((formSection) => (
          <button
            onClick={() => setSelectedSection(formSection.section)}
            key={formSection.section}
            className={cn(
              "flex flex-col items-center gap-2 p-1 hover:bg-background-tertiary border-r border-b min-w-[150px]",
              selectedSection === formSection.section &&
                "bg-background-tertiary border-0",
            )}
          >
            <div>{formSection.icon}</div>
            <div className="flex flex-col">
              <span className="text-md font-medium">{formSection.label}</span>
              <span>{formSection.value}</span>
            </div>
          </button>
        ))}
      </div>
      <div className="md:flex flex-col hidden w-1/3">
        {shiftFormSections.map((formSection) => (
          <button
            onClick={() => setSelectedSection(formSection.section)}
            key={formSection.section}
            className={cn(
              "flex flex-row items-center gap-4 p-2 hover:bg-background-tertiary border-r",
              selectedSection === formSection.section &&
                "bg-background-tertiary border-0",
            )}
          >
            <div>{formSection.icon}</div>
            <div className="flex flex-col items-start">
              <span className="text-lg font-medium">{formSection.label}</span>
              <span>{formSection.value}</span>
            </div>
          </button>
        ))}
        <div className="border-r flex-1"></div>
      </div>
      <Form {...form}>
        <form
          id="shift-form"
          onSubmit={handleSubmit}
          className="flex-grow relative bg-background-tertiary overflow-auto small-scrollbar"
        >
          {selectedSection === SHIFT_FORM_SECTIONS.service && (
            <div className="flex flex-col gap-10 p-4">
              <div className="flex flex-col gap-2">
                <FormField
                  control={form.control}
                  name="orderType"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between gap-2 relative">
                      <FormLabel>{t("OrderShiftForm.order_type")}</FormLabel>
                      <MultiSelect
                        className="min-w-[85px] max-w-[250px]"
                        maxSelect={1}
                        placeholder={t("OrderShiftForm.order_type_placeholder")}
                        onValueChange={(value) => {
                          field.onChange(value[0]);
                        }}
                        options={orderTypeOptions
                          .sort((a, b) => a.value.localeCompare(b.value))
                          .map((timeslotIntervalOption) => ({
                            label: timeslotIntervalOption.label,
                            value: timeslotIntervalOption.value,
                          }))}
                        defaultValue={field.value ? [field.value] : []}
                        value={field.value}
                      />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          )}
          {selectedSection === SHIFT_FORM_SECTIONS.time_and_day && (
            <div className="flex flex-col gap-10 p-4">
              <div className="flex flex-col gap-2">
                <Label
                  helperText="Select on which day this shift is active or starts."
                  size="lg"
                >
                  {t("Shift.date")}
                </Label>
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between gap-2 relative">
                      <FormLabel>{t("ShiftForm.start_date")}</FormLabel>
                      <div>
                        <VrodexPopover
                          buttonClassName="w-[150px]"
                          open={dateOpen}
                          onOpenChange={(open) => {
                            setDateOpen(open);
                            if (open) {
                              setStartTimeOpen(false);
                              setEndTimeOpen(false);
                            }
                          }}
                          value={format(field.value, "P")}
                        >
                          {(setPopoverOpen) => (
                            <FormControl>
                              <Calendar
                                className="w-full"
                                classNames={{
                                  caption_end: "w-full",
                                  row: "justify-between w-full flex",
                                  head_row: "justify-between flex w-full",
                                }}
                                locale={getDateFNSLocale(i18n.language)}
                                onDayClick={(date) => {
                                  field.onChange(
                                    set(date, {
                                      minutes: field.value.getMinutes(),
                                      hours: field.value.getHours(),
                                    }),
                                  );
                                  form.setValue(
                                    "startTime",
                                    set(date, {
                                      minutes: form
                                        .getValues("startTime")
                                        .getMinutes(),
                                      hours: form
                                        .getValues("startTime")
                                        .getHours(),
                                    }),
                                  );
                                  form.setValue(
                                    "endTime",
                                    set(date, {
                                      minutes: form
                                        .getValues("endTime")
                                        .getMinutes(),
                                      hours: form
                                        .getValues("endTime")
                                        .getHours(),
                                    }),
                                  );
                                  setPopoverOpen(false);
                                  setDateOpen(false);
                                }}
                                selected={field.value}
                              />
                            </FormControl>
                          )}
                        </VrodexPopover>
                      </div>
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label
                  helperText="Select the first bookable timeslot (start) and the last bookable timeslot (end)."
                  size="lg"
                >
                  {t("Shift.time")}
                </Label>
                <FormField
                  control={form.control}
                  name="startTime"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between gap-2 relative">
                      <FormLabel>{t("OrderShiftForm.start_time")}</FormLabel>
                      <div>
                        <VrodexPopover
                          buttonClassName="w-[100px]"
                          open={startTimeOpen}
                          onOpenChange={(open) => {
                            setStartTimeOpen(open);
                            if (open) {
                              setEndTimeOpen(false);
                              setDateOpen(false);
                            }
                          }}
                          value={format(field.value, "HH:mm")}
                        >
                          {(setPopoverOpen) => (
                            <TimePicker
                              value={field.value}
                              onChange={(value) => {
                                field.onChange(value);
                                setPopoverOpen(false);
                                calculateTimeslots({
                                  field: "start",
                                  date: value,
                                });
                              }}
                            />
                          )}
                        </VrodexPopover>
                      </div>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="endTime"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between gap-2 relative">
                      <FormLabel>{t("OrderShiftForm.end_time")}</FormLabel>
                      <div>
                        <VrodexPopover
                          buttonClassName="w-[100px]"
                          open={endTimeOpen}
                          onOpenChange={(open) => {
                            setEndTimeOpen(open);
                            if (open) {
                              setStartTimeOpen(false);
                              setDateOpen(false);
                            }
                          }}
                          value={format(field.value, "HH:mm")}
                        >
                          {(setPopoverOpen) => (
                            <TimePicker
                              value={field.value}
                              onChange={(value) => {
                                field.onChange(value);
                                setPopoverOpen(false);
                                calculateTimeslots({
                                  field: "end",
                                  date: value,
                                });
                              }}
                            />
                          )}
                        </VrodexPopover>
                      </div>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="timeslotInterval"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between gap-2 relative">
                      <FormLabel>{t("Shifts.timeslot_interval")}</FormLabel>
                      <MultiSelect
                        className="min-w-[85px] max-w-[150px]"
                        maxSelect={1}
                        placeholder={t("Shifts.timeslot_interval_placeholder")}
                        onValueChange={(value) => {
                          field.onChange(value[0]);
                        }}
                        options={timeslotIntervalOptions
                          .sort((a, b) => a.value - b.value)
                          .map((timeslotIntervalOption) => ({
                            label: timeslotIntervalOption.label,
                            value: timeslotIntervalOption.value,
                          }))}
                        defaultValue={field.value ? [field.value] : []}
                        value={field.value}
                      />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label
                  helperText="Make a shift occur more often by repeating it on selected days."
                  size="lg"
                >
                  {t("Shift.repeat")}
                </Label>
                <FormField
                  control={form.control}
                  name="repeat"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between gap-2 relative">
                      <FormLabel>{t("ShiftForm.repeat")}</FormLabel>
                      <div>
                        <SelectWithTrigger
                          className="w-[150px]"
                          value={field.value}
                          onValueChange={(value) => {
                            field.onChange(value);
                            if (value === "never") {
                              form.setValue("endDate", undefined);
                            }
                          }}
                        >
                          <FormControl>
                            <SelectValue />
                          </FormControl>
                          <SelectContent>
                            {repeatOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                          <FormMessage />
                        </SelectWithTrigger>
                      </div>
                    </FormItem>
                  )}
                />
                {repeat === ShiftRepeat.weekly && (
                  <FormField
                    control={form.control}
                    name="repeatDays"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between gap-2 relative">
                        <FormLabel>{t("Shift.repeat_on")}</FormLabel>
                        <div className="flex flex-row gap-1">
                          {repeatOnOptions.map((option) => (
                            <Button
                              onClick={() => {
                                if (field.value.includes(option.value)) {
                                  field.onChange(
                                    field.value.filter(
                                      (obj) => obj !== option.value,
                                    ),
                                  );
                                } else {
                                  field.onChange([
                                    ...field.value,
                                    option.value,
                                  ]);
                                }
                              }}
                              variant={
                                repeatDays.includes(option.value)
                                  ? "success"
                                  : "default"
                              }
                              className="rounded-full p-1 w-8 h-8"
                              key={option.value}
                            >
                              {option.label}
                            </Button>
                          ))}
                        </div>
                      </FormItem>
                    )}
                  />
                )}
              </div>
              {repeat === ShiftRepeat.weekly && (
                <div className="flex flex-col gap-2">
                  <Label size="lg">{t("Shift.end")}</Label>
                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between gap-2 relative">
                        <div className="flex-1">
                          <RadioGroup>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                onClick={() => {
                                  field.onChange(undefined);
                                }}
                                value="option-one"
                                id="option-one"
                                checked={!endDate}
                              />
                              <Label htmlFor="option-one">
                                {t("Shift.end_never")}
                              </Label>
                            </div>
                            <div className="flex items-center gap-10">
                              <div className="flex items-center gap-2">
                                <RadioGroupItem
                                  onClick={() => {
                                    field.onChange(
                                      shift?.endDate ?? new Date(),
                                    );
                                  }}
                                  value="option-two"
                                  id="option-two"
                                  checked={!!endDate}
                                />
                                <Label htmlFor="option-two">
                                  {t("Shift.end_on")}
                                </Label>
                              </div>
                              <div>
                                <VrodexPopover
                                  disabled={!endDate}
                                  buttonClassName="w-[150px]"
                                  open={endDateOpen}
                                  onOpenChange={(open) => {
                                    setEndDateOpen(open);
                                    if (open) {
                                      setDateOpen(false);
                                      setStartTimeOpen(false);
                                      setEndTimeOpen(false);
                                    }
                                  }}
                                  value={format(field.value ?? new Date(), "P")}
                                >
                                  {(setPopoverOpen) => (
                                    <FormControl>
                                      <Calendar
                                        className="w-full"
                                        classNames={{
                                          caption_end: "w-full",
                                          row: "justify-between w-full flex",
                                          head_row:
                                            "justify-between flex w-full",
                                        }}
                                        locale={getDateFNSLocale(i18n.language)}
                                        onDayClick={(date) => {
                                          field.onChange(date);
                                          setPopoverOpen(false);
                                          setDateOpen(false);
                                        }}
                                        selected={field.value}
                                      />
                                    </FormControl>
                                  )}
                                </VrodexPopover>
                              </div>
                            </div>
                          </RadioGroup>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>
          )}
        </form>
      </Form>
    </div>
  );
}
